import { useMemo } from 'react'
import { useConfiguratorConfigOptional } from '~/context/urlConfigContext'
import { useLocale } from '~/hooks/useLocale'
import { buildConfigShareUrl } from '~/utils/configShareUrl'
import type { ProductKey } from '~/utils/configUrl'

/**
 * Canonical shareable link for the configurator currently on screen.
 *
 * The configurator never writes its state back into the address bar
 * (`useUrlRead` is read-only), so `window.location.href` describes the config
 * the page was *opened* with — not the one the user just built. Anything that
 * hands a link to the customer (WhatsApp, webchat, share modal) must rebuild it
 * from the config context instead. Falls back to the current URL outside a
 * configurator route or when the product key is unknown.
 */
export function useConfigShareUrl(product?: ProductKey): string {
  const ctx = useConfiguratorConfigOptional()
  const locale = useLocale()

  return useMemo(() => {
    if (ctx && product) return buildConfigShareUrl(ctx.config, product, locale)
    return typeof window !== 'undefined' ? window.location.href : ''
  }, [ctx, product, locale])
}
